import React, { useState } from 'react'
import { View, Platform, Pressable } from 'react-native'
import Typography from '@ui/typography'
import Popover from '@ui/components/Popover'
import Glass from '@ui/elements/Glass'
import useAnimatedToggle from '@ui/hooks/useAnimatedToggle'
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import { colors } from '@ui/colors'
import VersionDetails from './VersionDetails'
import {
  AllVersionStatus,
  AppVersionProps,
  colorBack,
  colorText,
} from './types'
import cn from '@ui/utils/cn'
import BlurEffect from '@ui/effects/BlurEffect'
import Button from '@ui/buttons/Button'
import { useTranslation } from 'react-i18next'
import Clock from '@ui/elements/Clock'
import { ModalBottom } from '@ui/components/Modal'
import AppConfigDetails from './AppConfigDetails'
import { useRouter } from 'expo-router'
import AppConfig from 'config'
import DebugPanelButton from 'features/debug/DebugPanelButton'

interface Props {
  version: string
  status: AllVersionStatus
  apps: AppVersionProps[]
}

const sContainer = 'w-20 h-8'
const sRounded = 'rounded-t-md rounded-b-0'
const isWeb = Platform.OS === 'web'

export default function VersionComponent({ version, status, apps }: Props) {
  const { t } = useTranslation(['ui'])
  const [open, setOpen] = useState(false)
  const router = useRouter()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const popoverWidth = useSharedValue(360)
  const animationOpen = useAnimatedToggle(open)

  const styleText = useAnimatedStyle(() => {
    return {
      color: interpolateColor(
        animationOpen.value,
        [0, 1],
        [colorText[status], colors.white]
      ),
    }
  })

  const styleBackground = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        animationOpen.value,
        [0, 1],
        [colorBack[status], colors.black]
      ),
    }
  })

  const showDetails = () => {
    setDetailsOpen(true)
    setOpen(false)
  }

  return (
    <>
      <View className="absolute bottom-0 right-8">
        <Popover
          offset={Platform.select({ default: [1, 0], web: [1, 3] })}
          position={['top', 'right']}
          open={open}
          onClose={() => setOpen(false)}
          customWidth={popoverWidth}
          content={
            <Glass
              blur={50}
              className="flex w-full items-end justify-end rounded-2xl border border-black/10 bg-white/50"
            >
              {isWeb && (
                <div className="absolute left-0 top-0 h-full w-full backdrop-blur-2xl" />
              )}
              <View className="flex w-full flex-col">
                {apps.map((app, index) => (
                  <VersionDetails
                    key={app.title || index}
                    index={index}
                    onClose={() => setOpen(false)}
                    {...app}
                  />
                ))}
                <View className="flex flex-row items-center justify-end gap-2 border-t border-black/10 px-8 py-4">
                  <DebugPanelButton onOpen={() => setOpen(false)} />
                  {AppConfig.showAdmin && (
                    <Button
                      size="xs"
                      variant="outlined"
                      icon="start"
                      iconStart="settings"
                      onPress={() => {
                        setOpen(false)
                        router.navigate('/admin')
                      }}
                    />
                  )}
                  <Button
                    variant="outlined"
                    icon="start"
                    size="xs"
                    iconStart="info-circle"
                    onPress={showDetails}
                  />
                </View>
                <View className="flex flex-row items-center justify-between gap-2 border-t border-black/10 px-8 py-6">
                  <Typography
                    variant={Platform.select({ default: 'XSB', web: 'SMB' })}
                  >
                    <Clock
                      options={{ dateStyle: 'short', timeStyle: 'medium' }}
                      locale={'de'}
                    />
                  </Typography>
                  {isWeb && (
                    <Button
                      size="xs"
                      variant="outlined"
                      label={t('ui:refresh')}
                      className="w-max self-start"
                      onPress={() => window.location.reload()}
                    />
                  )}
                </View>
              </View>
            </Glass>
          }
        >
          <Pressable onPress={() => setOpen(true)}>
            <Animated.View
              className={cn(
                sRounded,
                sContainer,
                'flex flex-row items-center justify-center gap-4 overflow-hidden px-4 py-1.5',
                isWeb && 'border border-black/10'
              )}
              style={[
                {
                  transform: [
                    { translateY: Platform.select({ default: 0, web: 2 }) },
                  ],
                },
              ]}
            >
              <BlurEffect
                intensity={50}
                tint="light"
                className={cn('inset-0', sRounded, sContainer)}
              />
              <Animated.View
                className={cn('absolute left-0 top-0', sContainer, sRounded)}
                style={styleBackground}
              />
              <Typography
                variant={Platform.select({ default: 'SMB', web: 'SMB' })}
                className="select-none font-text-bold font-bold tracking-widest"
                style={[styleText]}
              >
                {version}
              </Typography>
            </Animated.View>
          </Pressable>
        </Popover>
      </View>
      <ModalBottom
        hideBottomSpace
        open={detailsOpen}
        close={() => setDetailsOpen(false)}
      >
        <AppConfigDetails apps={apps} />
      </ModalBottom>
    </>
  )
}
